import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Radio, message } from 'antd';
import { addWorkOrder, WorkOrderAddRequest, getCustomerUsers } from '@/api/workorder';
import { getDevice } from '@/api';
import { useRequest } from 'ahooks';

interface CreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateModal: React.FC<CreateModalProps> = ({ open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [customerList, setCustomerList] = useState<{ id: string; nickName: string; username: string }[]>([]);
  const { data: deviceData } = useRequest(getDevice, { defaultParams: [{ isAll: true }] });

  useEffect(() => {
    if (open) {
      form.resetFields();
      getCustomerUsers().then((resp: any) => {
        if (resp.success && resp.result) {
          setCustomerList(resp.result);
        }
      });
    }
  }, [open, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      // 将 billing_mode 写入 description 传给后端（当工单类型为安装时计费模式必填）
      const payload: any = { ...values };
      if ((values.orderType === 'install' || values.orderType === 'replace_core') && values.billingMode) {
        payload.description = `计费模式:${values.billingMode === 'days' ? '按天计费' : '按流量计费'}` + (values.description ? `|${values.description}` : '');
      }
      delete payload.billingMode;
      const resp = await addWorkOrder(payload as WorkOrderAddRequest);
      if (resp.success) {
        messageApi.success('创建成功');
        onSuccess();
      } else {
        messageApi.error(resp.errorMsg || '创建失败');
      }
    } catch {}
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="新建工单"
        open={open}
        onOk={handleOk}
        onCancel={onClose}
        okText="确定"
        cancelText="取消"
        maskClosable={false}
        width={600}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="工单类型" name="orderType" rules={[{ required: true, message: '请选择工单类型' }]}>
            <Select
              placeholder="请选择工单类型"
              options={[
                { value: 'install', label: '安装' },
                { value: 'recharge', label: '充值' },
                { value: 'replace_core', label: '换芯' },
                { value: 'remove', label: '撤机' },
                { value: 'change', label: '变更' },
              ]}
            />
          </Form.Item>
          <Form.Item label="客户" name="customerId" rules={[{ required: true, message: '请选择客户' }]}>
            <Select
              placeholder="请选择客户"
              options={customerList.map((c) => ({ value: c.id, label: c.nickName || c.username }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item label="设备ID" name="deviceId">
            <Select
              options={deviceData?.result?.list || []}
              allowClear
              showSearch
              optionFilterProp="name"
              placeholder="请选择设备"
              fieldNames={{ label: 'name', value: 'id' }}
            />
          </Form.Item>
          <Form.Item label="优先级" name="priority" initialValue={3}>
            <Select
              options={[
                { value: 1, label: '紧急' },
                { value: 2, label: '高' },
                { value: 3, label: '中' },
                { value: 4, label: '低' },
              ]}
            />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <Input.TextArea rows={3} placeholder="请输入工单描述" />
          </Form.Item>
          <Form.Item label="计费模式" name="billingMode"
            tooltip={'当工单类型为"安装"或"换芯"时，完成工单后净水机将按此模式计费'}
          >
            <Radio.Group>
              <Radio value="flow">按流量计费</Radio>
              <Radio value="days">按天计费</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="位置" name="location">
            <Input placeholder="请输入位置信息" />
          </Form.Item>
          <Form.Item label="联系人" name="contactName">
            <Input placeholder="请输入联系人" />
          </Form.Item>
          <Form.Item label="联系电话" name="contactPhone">
            <Input placeholder="请输入联系电话" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CreateModal;
